import { hasAvailableTourBundleRooms } from './tourBundleRoomAvailability'

interface FindCheapestTourV2PurchasableOptionOptions {
  prioritisedRoomType?: App.Tours.V2OfferRoomType
  shouldPrioritiseRoomType?: boolean
  departurePredicate?: (departure: App.Tours.TourV2OfferDeparture | undefined) => boolean
  purchasableOptionPredicate?: (purchasableOption: App.Tours.TourV2OfferPurchasableOption) => boolean
  getEffectivePrice?: (purchasableOption: App.Tours.TourV2OfferPurchasableOption) => number
}

export default function findCheapestTourV2PurchasableOption(
  offer: App.Tours.TourV2OfferSummary | App.Tours.TourV2Offer,
  selectedVariationId?: App.Tours.TourV2OfferVariation['id'],
  isLuxPlusToursAvailable = false,
  options: FindCheapestTourV2PurchasableOptionOptions = {},
): App.Tours.TourV2OfferPurchasableOption | undefined {
  const {
    prioritisedRoomType = 'twin',
    shouldPrioritiseRoomType = true,
    departurePredicate = departure => departure?.status === 'available',
    purchasableOptionPredicate = () => true,
    getEffectivePrice = purchasableOption => isLuxPlusToursAvailable && purchasableOption.memberPrice
      ? purchasableOption.memberPrice
      : purchasableOption.price,
  } = options
  const { departures, purchasableOptions } = offer || {}

  if (!departures || Object.keys(departures).length === 0 || purchasableOptions.length === 0) {
    return undefined
  }

  const cheapestPurchasableOptions = purchasableOptions.reduce<{
    other?: App.Tours.TourV2OfferPurchasableOption
    prioritised?: App.Tours.TourV2OfferPurchasableOption
  }>((
    cheapest,
    purchasableOption,
  ) => {
    const departure = departures[purchasableOption.fkDepartureId]
    // Provisional cart pricing can omit departures, but bundled rooms must be verified.
    if (!departure && (purchasableOption.ltleBundleId || ('ltleBundleId' in offer && offer.ltleBundleId))) return cheapest
    if (departure && (!hasAvailableTourBundleRooms(departure, 1) || (departure.ltleBundleId && purchasableOption.ltleBundleId !== departure.ltleBundleId))) return cheapest
    if (
      departurePredicate(departure) &&
      (!selectedVariationId || purchasableOption.fkVariationId === selectedVariationId) &&
      purchasableOptionPredicate(purchasableOption)
    ) {
      const purchasableOptionPrice = getEffectivePrice(purchasableOption)
      const cheapestPrioritisedPrice = cheapest.prioritised && getEffectivePrice(cheapest.prioritised)
      const cheapestOtherPrice = cheapest.other && getEffectivePrice(cheapest.other)
      if (
        shouldPrioritiseRoomType &&
        purchasableOption.roomType === prioritisedRoomType &&
        (cheapestPrioritisedPrice === undefined || purchasableOptionPrice < cheapestPrioritisedPrice)
      ) {
        return { ...cheapest, prioritised: purchasableOption }
      }

      if (cheapestOtherPrice === undefined || purchasableOptionPrice < cheapestOtherPrice) {
        return { ...cheapest, other: purchasableOption }
      }
    }

    return cheapest
  }, {
    other: undefined,
    prioritised: undefined,
  })

  return cheapestPurchasableOptions.prioritised ?? cheapestPurchasableOptions.other
}
